import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Skeleton from "react-loading-skeleton";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMusic, faTag } from "@fortawesome/free-solid-svg-icons";

import styled from "styled-components";

const MOTW_BANNER_URL = "//www.dreamchimney.com/api/tracks/message-board/banner/motw";

/** Accent for DJ name link — warm gold per MOTW layout reference */
const DJ_LINK_COLOR = "#000";

function isSoundCloudUrl(url) {
  if (!url || typeof url !== "string") return false;
  return url.toLowerCase().indexOf("soundcloud.com") !== -1;
}

/** Compact SoundCloud widget (166px) — `url` is the track page URL */
function soundCloudEmbedSrc(trackUrl) {
  let full = trackUrl.trim();
  if (full.startsWith("//")) full = `https:${full}`;
  else if (!/^https?:/i.test(full)) full = `https://${full}`;
  const encoded = encodeURIComponent(full);
  return `https://w.soundcloud.com/player/?url=${encoded}&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=false`;
}

function motwCoverUrl(seriesNumber) {
  if (seriesNumber === "" || seriesNumber == null) return "";
  return `//www.dreamchimney.com/motw/img/mixes-new/${seriesNumber}.jpg`;
}

/** Tags string may use spaces or commas — output normalized hashtags */
function tagsToHashtags(tags) {
  if (!tags || typeof tags !== "string") return [];
  return tags
    .split(/[\s,]+/)
    .map((t) => t.replace(/,/g, "").trim())
    .filter(Boolean)
    .map((t) => (t.startsWith("#") ? t : `#${t}`));
}

const MotwStack = styled.div`
  max-width: 100%;
`;

const MotwSubtitle = styled.p`
  font-size: 15px;
  line-height: 1.45;
  margin: 0 0 14px 0;
  color: #222;
`;

const DjLink = styled.a`
  color: ${DJ_LINK_COLOR};
  text-decoration: underline;
  font-weight: 500;

  &:hover {
    color: #b08d1f;
  }
`;

const EmbedWrap = styled.div`
  position: relative;
  margin-bottom: 0;
  min-height: 166px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #d8d8d8;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  background: #f0f0f0;
`;

const EmbedPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  color: #666;
  pointer-events: none;
  z-index: 2;
`;

const PulseIcon = styled.span`
  animation: motw-pulse 1.2s ease-in-out infinite;

  @keyframes motw-pulse {
    0%,
    100% {
      opacity: 0.55;
    }
    50% {
      opacity: 1;
    }
  }
`;

const EmbedIframe = styled.iframe`
  display: block;
  width: 100%;
  height: 166px;
  border: 0;
  opacity: ${(p) => (p.$loaded ? 1 : 0)};
  transition: opacity 0.5s ease;
  position: relative;
  z-index: 1;
  pointer-events: ${(p) => (p.$loaded ? "auto" : "none")};
`;

const MotwThumbLink = styled.a`
  display: block;
  overflow: hidden;
  background: #e8e8e8;
  aspect-ratio: 1;
  max-width: 400px;
  margin: 0 auto;
  border-radius: 4px;
  border: 1px solid #d8d8d8;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const MotwTagsRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  line-height: 1.5;
  color: #777;
  margin-top: 16px;

  .motw-tags-label {
    flex-shrink: 0;
    padding-top: 2px;
    color: #888;
  }

  .motw-tags-list {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 6px 6px;
    padding: 2px;
  }

  .motw-tags-list .motw-tag {
    white-space: nowrap;
  }
`;

function MotwSoundCloudEmbed({ embedUrl, title, dj }) {
  const rootRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  useEffect(() => {
    setIframeLoaded(false);
  }, [embedUrl]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || shouldLoad) return;

    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setShouldLoad(true);
          io.disconnect();
        }
      },
      { root: null, rootMargin: "120px 0px", threshold: 0 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [embedUrl, shouldLoad]);

  return (
    <EmbedWrap ref={rootRef}>
      {shouldLoad && (
        <EmbedIframe
          $loaded={iframeLoaded}
          width="100%"
          height="166"
          scrolling="no"
          title={title && dj ? `${title} by ${dj}` : "Mix of the Week"}
          allow="autoplay"
          src={embedUrl}
          onLoad={() => setIframeLoaded(true)}
        />
      )}
      {(!shouldLoad || !iframeLoaded) && (
        <EmbedPlaceholder aria-hidden>
          <PulseIcon>
            <FontAwesomeIcon icon={faMusic} />
          </PulseIcon>
          {shouldLoad ? "Loading player…" : "Player loads when in view…"}
        </EmbedPlaceholder>
      )}
    </EmbedWrap>
  );
}

const FeaturedCurrentMotw = () => {
  const [motw, setMotw] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await axios(MOTW_BANNER_URL);
        setMotw(res.data && typeof res.data === "object" ? res.data : null);
      } catch (e) {
        setMotw(null);
      }
      setLoading(false);
    }
    fetchData();
  }, []);

  const seriesNum = motw && motw["motw-series-number"];
  const hasMix = motw && seriesNum !== "" && seriesNum != null;
  const titleLower = (motw && motw.title ? motw.title : "").toLowerCase();
  const isGenericTitle = titleLower === "mix for dream chimney";
  const mixUrl = motw && motw.url;
  const useEmbed = hasMix && isSoundCloudUrl(mixUrl);
  const embedUrl = useEmbed ? soundCloudEmbedSrc(mixUrl) : "";
  const hashtagList = motw && motw.tags ? tagsToHashtags(motw.tags) : [];

  const gtagDj = (label) =>
    window.gtag &&
    window.gtag("event", "totd_click", {
      eventLabel: label,
    });

  return (
    <div className="totd-featured-box">
      <MotwStack>
        {loading && (
          <>
            <Skeleton height={22} style={{ marginBottom: 10, maxWidth: 280 }} />
            <Skeleton height={20} style={{ marginBottom: 16, maxWidth: 420 }} />
            <Skeleton height={166} />
            <Skeleton height={16} style={{ marginTop: 16, maxWidth: 500 }} />
          </>
        )}

        {!loading && hasMix && (
          <>
            <h2 className="page-header" style={{ marginBottom: 8 }}>
              <FontAwesomeIcon icon="headphones-alt" className="headphones-alt" />
              Mix of the Week #{seriesNum}
            </h2>
            <MotwSubtitle>
              This week's mix is {" "}
              {isGenericTitle ? (
                <>
                  from{" "}
                  <DjLink
                    href={motw.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => gtagDj(`Home MOTW DJ: ${motw.dj}`)}
                  >
                    {motw.dj}
                  </DjLink>
                </>
              ) : (
                <>
                  "{motw.title}" from {motw.dj}
                </>
              )}
            </MotwSubtitle>

            {useEmbed ? (
              <MotwSoundCloudEmbed
                key={embedUrl}
                embedUrl={embedUrl}
                title={motw.title}
                dj={motw.dj}
              />
            ) : (
              <MotwThumbLink
                href={motw.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => gtagDj(`Home MOTW: ${motw.dj}`)}
              >
                <img
                  src={motwCoverUrl(seriesNum)}
                  alt={`Mix of the Week #${seriesNum}`}
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </MotwThumbLink>
            )}

            {hashtagList.length > 0 && (
              <MotwTagsRow>
                <span className="motw-tags-label" aria-hidden>
                  <FontAwesomeIcon icon={faTag} style={{ marginRight: 6 }} />
                  Tags:
                </span>
                <span className="motw-tags-list">
                  {hashtagList.map((tag, i) => (
                    <span key={`motw-tag-${i}`} className="motw-tag">
                      {tag}
                    </span>
                  ))}
                </span>
              </MotwTagsRow>
            )}
          </>
        )}
      </MotwStack>
    </div>
  );
};

export default FeaturedCurrentMotw;
